import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, Mail } from "lucide-react";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";

const empty = { name: "", email: "", subject: "General Inquiry", message: "" };

export const ContactSection = () => {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Message sent to the Bandit crew! 🍭");
      setForm(empty);
    } catch (err) {
      toast.error("Could not send message. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <span className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-candy-teal">
            Custom orders · Holiday Bouquets · Wholesale
          </span>
          <h2 className="font-display text-5xl sm:text-6xl tracking-tight mt-1 text-candy-cream">
            HOLLER AT THE <span className="text-candy-pink">BANDIT</span>
          </h2>
          <p className="mt-3 font-body font-medium text-candy-cream/70 flex items-center justify-center gap-2">
            <Mail className="h-4 w-4" /> Sweettoothbanditshop@outlook.com
          </p>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          onSubmit={handleSubmit}
          className="spooky-card border-4 border-candy-pink rounded-2xl p-6 sm:p-8 shadow-brutal space-y-5"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="font-mono text-[11px] font-bold uppercase tracking-widest">Name</label>
              <input
                data-testid="contact-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full bg-candy-night text-candy-cream border-4 border-candy-teal rounded-lg px-3 py-2.5 font-medium focus:outline-none focus:shadow-brutal-teal transition-shadow placeholder:text-candy-cream/40"
              />
            </div>
            <div>
              <label className="font-mono text-[11px] font-bold uppercase tracking-widest">Email</label>
              <input
                data-testid="contact-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full bg-candy-night text-candy-cream border-4 border-candy-teal rounded-lg px-3 py-2.5 font-medium focus:outline-none focus:shadow-brutal-teal transition-shadow placeholder:text-candy-cream/40"
              />
            </div>
          </div>
          <div>
            <label className="font-mono text-[11px] font-bold uppercase tracking-widest">Subject</label>
            <select
              data-testid="contact-subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="brutal-select mt-1 w-full bg-candy-bg"
            >
              <option>General Inquiry</option>
              <option>Tray / Custom Box Order</option>
              <option>Custom Candy Box</option>
              <option>Merch / Pre-Order</option>
              <option>Wholesale / Events</option>
            </select>
          </div>
          <div>
            <label className="font-mono text-[11px] font-bold uppercase tracking-widest">Message</label>
            <textarea
              data-testid="contact-message"
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-1 w-full bg-candy-night text-candy-cream border-4 border-candy-teal rounded-lg px-3 py-2.5 font-medium focus:outline-none focus:shadow-brutal-teal transition-shadow placeholder:text-candy-cream/40"
            />
          </div>
          <button
            data-testid="contact-submit"
            type="submit"
            disabled={submitting}
            className="bg-candy-pink text-white font-bold uppercase tracking-wider py-3.5 px-8 border-4 border-black rounded-xl shadow-brutal-sm active:translate-x-1 active:translate-y-1 active:shadow-none transition-transform duration-150 flex items-center gap-2 disabled:opacity-60"
          >
            <Send className="h-5 w-5" /> {submitting ? "Sending…" : "Send Message"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
